/**
 * "Access App" / app-name links must open PRODUCTION when it exists.
 *
 * For an app promoted from staging, the catalog entity comes from the staging
 * backstage.yaml ("staging always wins", IDP-983), so its backstage.io/view-url
 * and Access App column point at {app}-staging.{domain}. This resolves the
 * production URL from the live /environment-status endpoint instead.
 *
 * Only apps that can have a staging environment (staging annotations or a
 * -staging host) trigger the lookup; every other app keeps its entity URL with
 * no extra request. Results are cached per app for the browser session so the
 * card grid / table don't call the endpoint once per render.
 */
import { type ReactElement, useEffect, useState } from 'react';
import type { Entity } from '@backstage/catalog-model';
import {
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { at: number; url: Promise<string | null> }>();

/** True when the entity may have a staging environment next to production. */
export function mayHaveStaging(entity: Entity, entityUrl?: string): boolean {
  const annotations = entity.metadata.annotations ?? {};
  return (
    annotations['microapp/stagingEnabled'] === 'true' ||
    annotations['microapp/stagingOnly'] === 'true' ||
    !!entityUrl?.includes('-staging.')
  );
}

/** Test hook: clear the per-session cache. */
export function clearMicroappLaunchUrlCache(): void {
  cache.clear();
}

/**
 * Returns the production URL when production is deployed, otherwise the
 * entity's own URL (staging-only apps keep their staging URL).
 */
export function useMicroappLaunchUrl(
  entity: Entity,
  entityUrl: string | undefined,
): string | undefined {
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const appName = entity.metadata.name;
  const lookup = mayHaveStaging(entity, entityUrl);
  const [prodUrl, setProdUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!lookup) return undefined;
    let cancelled = false;
    let entry = cache.get(appName);
    if (!entry || Date.now() - entry.at > CACHE_TTL_MS) {
      entry = {
        at: Date.now(),
        url: (async () => {
          try {
            const { token } = await identityApi.getCredentials();
            const response = await fetchApi.fetch(
              `/api/microapps-api/environment-status/${encodeURIComponent(
                appName,
              )}`,
              {
                headers: { ...(token && { Authorization: `Bearer ${token}` }) },
              },
            );
            if (!response.ok) return null;
            const data = (await response.json()) as {
              production?: { deployed?: boolean; url?: string | null };
            };
            return data.production?.deployed && data.production.url
              ? data.production.url
              : null;
          } catch {
            return null;
          }
        })(),
      };
      cache.set(appName, entry);
    }
    entry.url.then(url => {
      if (!cancelled) setProdUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [appName, lookup, identityApi, fetchApi]);

  return (lookup && prodUrl) || entityUrl;
}

/**
 * Render-prop version for places that can't call hooks directly (catalog
 * table column render functions).
 */
export const MicroappLaunchUrl = ({
  entity,
  url,
  children,
}: {
  entity: Entity;
  url: string | undefined;
  children: (href: string | undefined) => ReactElement | null;
}) => children(useMicroappLaunchUrl(entity, url));

/** Whether a custom column is the app's "Access App" link. */
export const isAccessAppColumn = (title: string | undefined): boolean =>
  !!title && title.replace(/\s+/g, ' ').trim() === 'Access App';
